import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log via console.error so Sentry's global handler can pick it up.
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = "/";
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { error } = this.state;

      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-white dark:bg-black text-gray-900 dark:text-gray-100 px-4">
          <div className="w-full max-w-md flex flex-col items-center text-center gap-6">
            {/* Logo */}
            <div className="w-10 h-10 rounded-md bg-black dark:bg-white flex items-center justify-center">
              <span className="text-white dark:text-black text-base font-semibold leading-none">
                R
              </span>
            </div>

            {/* Heading + sub */}
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Something went wrong
              </h1>
              <p className="text-sm text-gray-500">
                An unexpected error occurred. Try refreshing the page.
              </p>
            </div>

            {/* Dev-only error details */}
            {import.meta.env.DEV && error && (
              <pre className="w-full text-left text-xs font-mono bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1f1f22] rounded-md p-3 overflow-auto whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
                {error.message}
              </pre>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={this.handleRefresh}>
                Refresh page
              </Button>
              <Button variant="secondary" onClick={this.handleGoHome}>
                Go home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
